import * as React from "react";
import { Container, Form, FormButton, FormDropdown, FormField, Message } from "@/components/bp/bp-ui";
import { FormikProps, Formik } from "formik";
import { FormErrorList } from "../FormErrorList";

import { AlleleFormat, CaseClient, EvidenceClient, LabKitClient, SampleClient } from "@app/api2/api";
import * as yup from "yup";
import SemanticField from "../../SemanticField";
import { FileUpload, IUploadedFile } from "../FileUpload";
import { IDropdownItem } from "../IDropdownItem";
import { PageHeader } from "@common/PageHeader";
import { insertComponentsIntoText } from "@common/Utils";
import { buildUrl, AppRoutes } from "../AppRoutes";
import { Link } from "react-router-dom";
import { ChangeObserver } from "../Labs/ChangeObserver";
import { getCaseAlleleFormat, toAlleleFormatName } from "@common/AlleleFormatNames";
import { TranslationContext } from "../Contexts/TranslationContext";
import { BPContainer } from "@/components/bp/bpcontainer";

// type AlleleFormat = "CE_RU" | "LUS" | "LUSP";
export class SampleCreateFormValues {
    public kitId: number;
    public name: string;
    public file: IUploadedFile;
    public format?: AlleleFormat;
}

export class SampleCreateProps {
    public labId: number;
    public caseId: number;
}

export class SampleCreateState {
    public formData: SampleCreateFormValues = new SampleCreateFormValues();
    public loading: boolean = true;
    public kitList: Array<IDropdownItem<number>> = [];
    serverErrors: string[];
    showServerErrors: boolean = false;

    previewErrors: string[];
    showPreviewErrors: boolean = false;

    showSampleNames: boolean = false;
    sampleNames: Array<{ name: string, message: string }> = [];

    caseName: string;
    isUAS: boolean;
    caseAlleleFormat: AlleleFormat;
}

export class SampleCreate extends React.Component<SampleCreateProps, SampleCreateState> {


    private createSchema = yup.object().shape({
        kitId: yup.number().required().label(window.translation.common.kit),
        file: yup.object().required().shape({
            fileName: yup.string().required(window.translation.sample.sampleFile),
        }).nullable(),
    });


    private kitClient = new LabKitClient();
    private caseClient = new CaseClient();
    private sampleClient = new SampleClient();
    private evidenceClient = new EvidenceClient();

    constructor(props, context) {
        super(props, context);


        this.state = new SampleCreateState();
    }

    public render() {

        return (
            <BPContainer>
                <PageHeader header={insertComponentsIntoText(window.translation.sampleView.uploadTitle, {
                    '{sampleName}': this.state.formData.name,
                    '{caseName}': <Link key={'caseLink'} to={buildUrl(AppRoutes.CaseDetails, {
                        caseId: this.props.caseId,
                        labId: this.props.labId
                    })}>{this.state.caseName}</Link>
                })} />

                <Formik initialValues={this.state.formData} onSubmit={this.submitCreate} validationSchema={this.createSchema}>
                    {(props) => {
                        return this.renderCreateForm(props);
                    }}
                </Formik>
            </BPContainer>
        );

    }

    public async componentDidMount(): Promise<any> {
        const kits = await this.caseClient.getCaseKits(this.props.labId, this.props.caseId);


        const evidence = await this.evidenceClient.getForUpload(this.props.labId, this.props.caseId);
        let alleleFormat = await getCaseAlleleFormat({ labId: this.props.labId, caseId: this.props.caseId })

        this.setState({
            caseName: evidence.caseName,
            caseAlleleFormat: alleleFormat
        });


        this.setState({
            loading: false,
            kitList: kits.map((k) => ({ key: k.id, value: k.id, text: k.fullName })),
            // caseAlleleFormat: alleleFormat
        });
    }
    private clearFilePreview = async () => {
        this.setState({
            isUAS: false,
            showPreviewErrors: false,
            showSampleNames: false,
            sampleNames: [],
            previewErrors: []
        });
    }

    private getFilePreview = async (file: IUploadedFile, kitId: number, format: AlleleFormat) => {
        const result = await this.sampleClient.preview(this.props.labId, this.props.caseId,
            {
                kitId: kitId,
                sampleFile: file.contents,
                format: format
            });

        if (!result.successful) {
            this.setState({
                isUAS: result.isUAS,
                showPreviewErrors: (result.errors || []).length > 0,
                showSampleNames: false,
                sampleNames: [],
                previewErrors: (result.errors || []).map((e) => (e.value))
            });
        } else {
            this.setState({
                isUAS: result.isUAS,
                showPreviewErrors: (result.errors || []).length > 0,
                showSampleNames: true,
                previewErrors: (result.errors || []).map((e) => (e.value)),
                sampleNames: result.newSample.map((e) => {
                    return { name: e.name, message: e.warning };
                })
            });
        }
    };

    private renderCreateForm = (props: FormikProps<SampleCreateFormValues>) => {
        const handleFileChange = async (name, data) => {
            await props.setFieldTouched(name);
            await props.setFieldValue(name, data);
            this.setState({ formData: { ...this.state.formData, [name]: data } });
        };

        const onFormChange = (values: SampleCreateFormValues) => {
            if (values.file)
                this.getFilePreview(values.file, values.kitId, values.format)
            else
                this.clearFilePreview()
        }

        const canSubmit = () => {
            return props.isValid && (!this.state.showPreviewErrors);
        };

        const formats = [this.state.caseAlleleFormat].map(x => {
            return {
                key: `form${x}`, value: x, text: toAlleleFormatName(x)
            }
        })

        const errorList = Object.getOwnPropertyNames(props.errors)
            .filter((k) => props.touched[k])
            .map((k) => props.errors[k]);

        return (<TranslationContext.Consumer>{(t) => <Form loading={this.state.loading} onSubmit={props.handleSubmit}>
            <div className="ui segment">
                <ChangeObserver value={props.values} onChange={values => onFormChange(values)} />

                <SemanticField component={FormDropdown} name="kitId" selection options={this.state.kitList} label={t.common.kit} value={props.values.kitId} error={props.errors.kitId && props.touched.kitId} />
                <FormField>
                    <label>{t.sample.sampleFile}</label>
                    <FileUpload placeholderText={t.sample.sampleFile} name="file" fileUploaded={handleFileChange} value={props.values.file} />
                </FormField>
                {this.state.isUAS &&
                    <SemanticField component={FormDropdown} name="format" selection options={formats} label={t.sample.sampleInUAS} value={props.values.format} error={props.errors.format && props.touched.format} />
                }

                <FormButton disabled={!canSubmit()}>{t.common.save}</FormButton>

                <Message error header={t.common.errorsOnForm} list={errorList} visible={errorList.length > 0} />
                <FormErrorList message={t.sample.fileErrors} show={this.state.showServerErrors} errors={this.state.serverErrors} />
                <FormErrorList message={t.sample.fileCannotBeProcessed} show={this.state.showPreviewErrors} errors={this.state.previewErrors} />
                {this.state.showSampleNames && <Message info header={t.sample.sampleNames} list={(this.state.sampleNames ?? []).map((m, i) => `${m.name} ${m.message ? `(${m.message})` : ""}`)} />}
            </div>

        </Form>}</TranslationContext.Consumer>);
    };

    private submitCreate = async (values: SampleCreateFormValues): Promise<any> => {
        this.setState({ loading: true });
        const result = await this.sampleClient.create(this.props.labId, this.props.caseId,
            {
                kitId: values.kitId,
                sampleFile: values.file.contents,
                format: values.format
            });

        if (result.successful) {
            window.location.assign(result.url);
        } else {
            this.setState({
                loading: false,
                serverErrors: result.errors.map((e) => e.value),
                showServerErrors: true,
            });
        }
    };

}
